import React, { useContext } from "react";

import AssessmentIcon from "@mui/icons-material/Assessment";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import DomainIcon from "@mui/icons-material/Domain";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import HistoryIcon from "@mui/icons-material/History";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import SchemaIcon from "@mui/icons-material/Schema";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import StoreIcon from "@mui/icons-material/Store";
import TranslateIcon from "@mui/icons-material/Translate";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Environment } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { PERISCOPE_USER_GUIDE_LINK } from "../../../constants/index";
import { PermissionsContext } from "../../../context/permissions-context";
import { openDevToolsOverlay } from "../../../helpers/dev-tools-overlay-bridge";
import { getEnvironment } from "../../../helpers/environment";
import CollapsibleNavItem from "./collapsible-nav-item";
import NavItemSkeleton from "./nav-item-skeleton";

// The real number of nav items is permission-dependent and unknown while
// permissions load, so we show a fixed, generous number of placeholders.
const NAV_ITEM_SKELETON_COUNT = 8;

const NavList = ({ collapsed = false }: { collapsed?: boolean }) => {
  const { hasPermissions, permissions } = useContext(PermissionsContext);

  const isPeriscopeAdmin = hasPermissions([
    ActionType.CREATE_GROUP,
    ActionType.ADD_ACTION,
  ]);
  const isCorpDomainUser = hasPermissions([ActionType.LIST_CORP_DOMAINS]);
  const env = getEnvironment();
  const isDevOrSandpit =
    env === Environment.DEVELOPMENT || env === Environment.SANDPIT;
  const isSeoTemplateUser = hasPermissions([
    ActionType.UPLOAD_TEMPLATE_TO_CROWDIN,
  ]);
  const isSupportManager = hasPermissions([ActionType.UPDATE_USER_ADMIN_FLAG]);
  const isWorkflowUser =
    hasPermissions([ActionType.LIST_SUPPORT_WORKFLOW_EXECUTIONS]) ||
    hasPermissions([ActionType.LIST_WORKFLOW_EXECUTIONS]);
  const isMarketplaceUser = hasPermissions([
    ActionType.LIST_MARKETPLACE_PRODUCT_ATTR_GEN_JOBS,
    ActionType.ADD_MARKETPLACE_PRODUCT_ATTR_GEN_JOB,
    ActionType.GET_MARKETPLACE_PRODUCT_ATTR_JOB_RESULT,
  ]);
  const isRetroShareUser = hasPermissions([ActionType.LIST_RETROSHARE]);
  const isSCCareUser = isSupportManager;

  return (
    <>
      {permissions.isPermissionsLoading ? (
        Array.from({ length: NAV_ITEM_SKELETON_COUNT }, (_, index) => (
          <NavItemSkeleton key={index} collapsed={collapsed} />
        ))
      ) : (
        <>
          <CollapsibleNavItem
            icon={<SearchIcon />}
            label="Search"
            to="/"
            collapsed={collapsed}
          />

          {isCorpDomainUser && (
            <CollapsibleNavItem
              icon={<DomainIcon />}
              label="Corp Domains"
              to="/corp-domains"
              collapsed={collapsed}
            />
          )}

          {/* Reports section - sudo history, also other audit logs to be surfaced here once implemented*/}
          <CollapsibleNavItem
            icon={<AssessmentIcon />}
            label="Reports"
            to="/reports"
            collapsed={collapsed}
          />

          <CollapsibleNavItem
            icon={<HelpOutlineOutlinedIcon />}
            label="Help"
            href={PERISCOPE_USER_GUIDE_LINK}
            collapsed={collapsed}
          />

          {/* Support Tools - only support managers will see on navbar */}
          {isSupportManager && (
            <CollapsibleNavItem
              icon={<HomeRepairServiceIcon />}
              label="Support Tools"
              to="/admin-tools"
              collapsed={collapsed}
            />
          )}

          {/* SEO Template Translations - only those with crowdin upload action access will see on navbar*/}
          {isSeoTemplateUser && (
            <CollapsibleNavItem
              icon={<TranslateIcon />}
              label="SEO Templates"
              to="/seo-templates"
              collapsed={collapsed}
            />
          )}

          {/* Workflows - only those with workflow access will see on navbar*/}
          {isWorkflowUser && (
            <CollapsibleNavItem
              icon={<SchemaIcon />}
              label="Workflows"
              to="/workflows"
              collapsed={collapsed}
            />
          )}

          {/* Marketplace - only those with marketplace access will see on navbar */}
          {isMarketplaceUser && (
            <CollapsibleNavItem
              icon={<StoreIcon />}
              label="Marketplace"
              to="/marketplace"
              collapsed={collapsed}
            />
          )}

          {/* SC Care - only those with provision SC Care access will see on navbar */}
          {isSCCareUser && (
            <CollapsibleNavItem
              icon={<HealthAndSafetyIcon />}
              label="SC Care"
              to="/sc-care"
              collapsed={collapsed}
            />
          )}

          {/* Retroshares - will only display if you have permissions for LIST_RETROSHARE */}
          {isRetroShareUser && (
            <CollapsibleNavItem
              icon={<HistoryIcon />}
              label="Retroshares"
              to="/retroshare"
              collapsed={collapsed}
            />
          )}

          {/* Admin settings - only those with admin action access will see on navbar*/}
          {isPeriscopeAdmin && (
            <CollapsibleNavItem
              icon={<SettingsIcon />}
              label="Admin"
              to="/settings"
              collapsed={collapsed}
            />
          )}

          {/* Dev Tools - only in dev/sandpit; mirrors the org-scoped button gate.
              Opens the overlay (same as the user-menu item and Ctrl/Cmd+Shift+`)
              rather than routing, so it doesn't navigate away from the current
              page. */}
          {isDevOrSandpit && (
            <CollapsibleNavItem
              icon={<DeveloperBoardIcon />}
              label="Dev Tools"
              tooltip="Dev Tools (⌘⇧`)"
              onClick={() => openDevToolsOverlay()}
              collapsed={collapsed}
              dataTestId="nav-dev-tools-link"
            />
          )}
        </>
      )}
    </>
  );
};
export default NavList;
